import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  ButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  ViewList as ViewListIcon,
  Save as SaveIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Restaurant as RestaurantIcon,
  Category as CategoryIcon,
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Euro as EuroIcon,
  Home as HomeIcon,
  ShoppingBag as ShoppingBagIcon,
  LocalShipping as DeliveryIcon,
  MonetizationOn as PriceIcon
} from '@mui/icons-material';
import MenuService from '../../../firebase/MenuService';
import ImageMigrationPanel from '../ImageMigrationPanel';

// Iconos disponibles para categorías (organizados por tipo)
const AVAILABLE_ICONS = {
  'Comida Principal': ['🍔', '🍟', '🍕', '🌮', '🍜', '🍝', '🥘', '🍛', '🍱', '🥪', '🌯', '🥙', '🧆'],
  'Carnes y Pescados': ['🥩', '🍖', '🍗', '🥓', '🌭', '🍤', '🍣', '🥟', '🐟', '🦐', '🦀', '🦞'],
  'Vegetarianos': ['🥗', '🥑', '🥕', '🥒', '🌶️', '🌽', '🥦', '🥬', '🍅', '🥔', '🍠', '🧄', '🧅'],
  'Panadería': ['🥐', '🍞', '🥖', '🥨', '🧇', '🥞', '🍳'],
  'Postres': ['🍰', '🧁', '🍮', '🍭', '🍬', '🍩', '🍪', '🎂', '🍨', '🍧', '🍦', '🥧'],
  'Frutas': ['🍓', '🍌', '🍎', '🍊', '🍋', '🥝', '🍇', '🫐', '🍈', '🍒', '🍑', '🥭', '🍐', '🥥'],
  'Bebidas': ['☕', '🍵', '🥤', '🧃', '🥛', '🧋', '🍺', '🍻', '🍷', '🥂', '🍾', '🍸', '🍹', '🥃'],
  'Lácteos': ['🧀', '🥚', '🍯', '🥛'],
  'Varios': ['📋', '🍽️', '🥄', '🍴', '🔥', '⭐', '💎', '🎯', '🏆', '❤️']
};

const MenuAdminComponent = () => {
  // Estados principales
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [activeTab, setActiveTab] = useState(0); // 0: Productos, 1: Categorías

  // Estados para diálogos
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Estados para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Estados para gestión de categorías e iconos
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [selectedIconCategory, setSelectedIconCategory] = useState('Comida Principal');
  const [migrationStatus, setMigrationStatus] = useState(null);

  // Efecto para cargar datos iniciales
  useEffect(() => {
    loadInitialData();
  }, []);

  // Cargar datos iniciales
  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([fetchCategories(), fetchProducts()]);
    } catch (error) {
      console.error('Error al cargar datos iniciales:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar categorías
  const fetchCategories = async () => {
    try {
      const fetchedCategories = await MenuService.getCategories();
      setCategories(fetchedCategories || []);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      showSnackbar('Error al cargar categorías', 'error');
    }
  };

  // Cargar productos
  const fetchProducts = async () => {
    try {
      const fetchedProducts = await MenuService.getProducts();
      setProducts(fetchedProducts || []);
    } catch (error) {
      console.error('Error al cargar productos:', error);
      showSnackbar('Error al cargar productos', 'error');
    }
  };

  // Mostrar notificación
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // === FUNCIONES DE MIGRACIÓN ===
  const handleMigrateCategories = async () => {
    if (!window.confirm('¿Quieres migrar las categorías existentes para agregar iconos y ordenamiento?\n\nEsto solo debe hacerse UNA vez y es seguro.')) {
      return;
    }
    
    setIsLoading(true);
    setMigrationStatus('running');
    
    try {
      const result = await MenuService.migrateCategoriesData();
      
      if (result.success) {
        await fetchCategories();
        setMigrationStatus('success');
        showSnackbar(result.message, 'success');
      } else {
        setMigrationStatus('error');
        showSnackbar('Error en la migración', 'error');
      }
      
    } catch (error) {
      console.error('Error en migración:', error);
      setMigrationStatus('error');
      showSnackbar(`Error durante la migración: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
      setTimeout(() => setMigrationStatus(null), 3000);
    }
  };

  // 🆕 Migración de precios de productos
  const handleMigrateProductsPricing = async () => {
    if (!window.confirm('¿Quieres migrar los productos existentes para usar precios múltiples (local, takeaway, delivery)?\n\nEsto convertirá el precio actual en:\n- Local: precio actual\n- Takeaway: precio actual\n- Delivery: precio actual + 10%\n\n¿Continuar?')) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await MenuService.migrateProductsPricing();
      
      if (result.success) {
        await fetchProducts();
        showSnackbar(result.message, 'success');
      } else {
        showSnackbar('Error en la migración de precios', 'error');
      }
      
    } catch (error) {
      console.error('Error en migración de precios:', error);
      showSnackbar(`Error durante la migración de precios: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Producto vacío para crear nuevos
  const emptyProduct = {
    name: '',
    description: '',
    pricing: {
      local: '',
      takeaway: '',
      delivery: ''
    },
    price: '', // Para compatibilidad
    image: '',
    categoryId: categories.length > 0 ? categories[0].id : '',
    availability: {
      local: true,
      takeaway: true,
      delivery: true
    },
    active: true
  };

  // Categoría vacía para crear nuevos
  const emptyCategory = {
    name: '',
    icon: '📋',
    description: '',
    active: true
  };

  // Filtrar productos por categoría
  const filteredProducts = selectedCategory === 0 
    ? products 
    : products.filter(product => product.categoryId === selectedCategory);

  // === FUNCIONES PARA PRODUCTOS ===
  
  const handleAddProduct = () => {
    setCurrentProduct({ 
      ...emptyProduct, 
      categoryId: categories.length > 0 ? categories[0].id : '' 
    });
    setPreviewImage(null);
    setSelectedFile(null);
    setOpenProductDialog(true);
  };

  const handleEditProduct = (product) => {
    // Normalizar estructura de precios para el formulario
    const normalizedProduct = {
      ...product,
      pricing: product.pricing || {
        local: product.price || '',
        takeaway: product.price || '',
        delivery: product.price || ''
      }
    };
    
    setCurrentProduct(normalizedProduct);
    setPreviewImage(product.image);
    setSelectedFile(null);
    setOpenProductDialog(true);
  };

  const handleProductChange = (e) => {
    const { name, value } = e.target;
    setCurrentProduct(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 🆕 Manejar cambios en precios múltiples
  const handlePricingChange = (priceType, value) => {
    setCurrentProduct(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [priceType]: value
      },
      // Actualizar price para compatibilidad (usar precio local)
      price: priceType === 'local' ? value : prev.price
    }));
  };

  const handleAvailabilityChange = (type) => {
    setCurrentProduct(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [type]: !prev.availability[type]
      }
    }));
  };

  const handleImageSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreviewImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProduct = async () => {
    if (!currentProduct.name || !currentProduct.categoryId) {
      showSnackbar('Por favor, completa el nombre y selecciona una categoría', 'error');
      return;
    }

    // Validar que al menos un precio esté definido
    const { local, takeaway, delivery } = currentProduct.pricing;
    if (!local && !takeaway && !delivery) {
      showSnackbar('Por favor, ingresa al menos un precio', 'error');
      return;
    }

    setIsLoading(true);
    
    try {
      let productToSave = { 
        ...currentProduct,
        // Asegurar que los precios sean números
        pricing: {
          local: parseFloat(local) || 0,
          takeaway: parseFloat(takeaway) || 0,
          delivery: parseFloat(delivery) || 0
        },
        // Mantener price para compatibilidad (usar precio local)
        price: parseFloat(local) || 0
      };
      
      if (selectedFile) {
        productToSave.imageFile = selectedFile;
      }
      
      if (currentProduct.id) {
        await MenuService.updateProduct(currentProduct.id, productToSave);
        showSnackbar('Producto actualizado correctamente', 'success');
      } else {
        await MenuService.createProduct(productToSave);
        showSnackbar('Producto añadido correctamente', 'success');
      }
      
      await fetchProducts();
      setOpenProductDialog(false);
    } catch (error) {
      console.error('Error al guardar producto:', error);
      showSnackbar('Error al guardar el producto: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProduct = async (id, name) => {
    if (window.confirm(`¿Estás seguro de que deseas eliminar "${name}"?`)) {
      setIsLoading(true);
      
      try {
        await MenuService.deleteProduct(id);
        await fetchProducts();
        showSnackbar('Producto eliminado correctamente', 'success');
      } catch (error) {
        console.error('Error al eliminar producto:', error);
        showSnackbar('Error al eliminar el producto', 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleToggleActive = async (id, active) => {
    try {
      await MenuService.toggleProductActive(id, !active);
      await fetchProducts();
    } catch (error) {
      console.error('Error al cambiar estado del producto:', error);
      showSnackbar('Error al cambiar el estado del producto', 'error');
    }
  };

  // === FUNCIONES PARA CATEGORÍAS ===

  const handleAddCategory = () => {
    setCurrentCategory({ ...emptyCategory });
    setPreviewImage(null);
    setSelectedFile(null);
    setShowIconPicker(false);
    setOpenCategoryDialog(true);
  };

  const handleEditCategory = (category) => {
    setCurrentCategory({ ...category });
    setPreviewImage(category.image);
    setSelectedFile(null);
    setShowIconPicker(false);
    setOpenCategoryDialog(true);
  };

  const handleCategoryChange = (e) => {
    const { name, value } = e.target;
    setCurrentCategory(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIconSelect = (icon) => {
    setCurrentCategory(prev => ({
      ...prev,
      icon: icon
    }));
    setShowIconPicker(false);
  };

  const handleSaveCategory = async () => {
    if (!currentCategory.name.trim()) {
      showSnackbar('El nombre de la categoría es obligatorio', 'error');
      return;
    }
    
    setIsLoading(true);
    try {
      let categoryToSave = { ...currentCategory };
      
      if (selectedFile) {
        categoryToSave.imageFile = selectedFile;
      }
      
      if (currentCategory.id) {
        await MenuService.updateCategory(currentCategory.id, categoryToSave);
        showSnackbar('Categoría actualizada correctamente', 'success');
      } else {
        await MenuService.createCategory(categoryToSave);
        showSnackbar('Categoría añadida correctamente', 'success');
      }
      
      await fetchCategories();
      setOpenCategoryDialog(false);
    } catch (error) {
      console.error('Error al guardar categoría:', error);
      showSnackbar('Error al guardar la categoría: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCategory = async (id, name) => {
    const productsInCategory = products.filter(p => p.categoryId === id).length;
    
    if (productsInCategory > 0) {
      if (!window.confirm(`La categoría "${name}" tiene ${productsInCategory} productos. ¿Estás seguro de que deseas eliminarla?`)) {
        return;
      }
    } else {
      if (!window.confirm(`¿Estás seguro de que deseas eliminar la categoría "${name}"?`)) {
        return;
      }
    }
    
    setIsLoading(true);
    
    try {
      await MenuService.deleteCategory(id);
      await Promise.all([fetchCategories(), fetchProducts()]);
      showSnackbar('Categoría eliminada correctamente', 'success');
    } catch (error) {
      console.error('Error al eliminar categoría:', error);
      showSnackbar(error.message || 'Error al eliminar la categoría', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMoveCategoryUp = async (index) => {
    if (index === 0) return;
    
    const reorderedCategories = [...categories];
    [reorderedCategories[index - 1], reorderedCategories[index]] = 
    [reorderedCategories[index], reorderedCategories[index - 1]];
    
    try {
      await MenuService.reorderCategories(reorderedCategories.map(cat => cat.id));
      await fetchCategories();
    } catch (error) {
      console.error('Error al reordenar categorías:', error);
      showSnackbar('Error al reordenar las categorías', 'error');
    }
  };

  const handleMoveCategoryDown = async (index) => {
    if (index === categories.length - 1) return;
    
    const reorderedCategories = [...categories];
    [reorderedCategories[index], reorderedCategories[index + 1]] = 
    [reorderedCategories[index + 1], reorderedCategories[index]];
    
    try {
      await MenuService.reorderCategories(reorderedCategories.map(cat => cat.id));
      await fetchCategories();
    } catch (error) {
      console.error('Error al reordenar categorías:', error);
      showSnackbar('Error al reordenar las categorías', 'error');
    }
  };

  // === COMPONENTES DE RENDERIZADO ===

  const renderMigrationButtons = () => {
    const needsCategoryMigration = categories.some(cat => !cat.icon || !cat.order);
    const needsPricingMigration = products.some(prod => !prod.pricing);
    
    if (!needsCategoryMigration && !needsPricingMigration && migrationStatus !== 'running') {
      return null;
    }

    return (
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {needsCategoryMigration && (
          <Tooltip title="Actualizar categorías existentes con iconos y ordenamiento">
            <Button
              variant="outlined"
              color="warning"
              size="small"
              onClick={handleMigrateCategories}
              disabled={isLoading}
              startIcon={migrationStatus === 'running' ? <CircularProgress size={16} /> : <SyncIcon />}
            >
              🔄 Migrar Categorías
            </Button>
          </Tooltip>
        )}
        
        {needsPricingMigration && (
          <Tooltip title="Convertir productos a sistema de precios múltiples">
            <Button
              variant="outlined"
              color="info"
              size="small"
              onClick={handleMigrateProductsPricing}
              disabled={isLoading}
              startIcon={<PriceIcon />}
            >
              💰 Migrar Precios
            </Button>
          </Tooltip>
        )}
      </Box>
    );
  };

  // 🆕 Renderizar precios múltiples en vista de tabla
  const renderPricingInTable = (product) => {
    if (product.pricing) {
      return (
        <Box>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            <HomeIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
            {parseFloat(product.pricing.local || 0).toFixed(2)}€
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            <ShoppingBagIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
            {parseFloat(product.pricing.takeaway || 0).toFixed(2)}€
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            <DeliveryIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
            {parseFloat(product.pricing.delivery || 0).toFixed(2)}€
          </Typography>
        </Box>
      );
    } else {
      return `${parseFloat(product.price || 0).toFixed(2)}€`;
    }
  };

  // 🆕 Renderizar precios múltiples en vista de cuadrícula
  const renderPricingInGrid = (product) => {
    if (product.pricing) {
      return (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem' }}>
              <HomeIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Local:
            </Typography>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
              {parseFloat(product.pricing.local || 0).toFixed(2)}€
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem' }}>
              <ShoppingBagIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Takeaway:
            </Typography>
            <Typography variant="body2" color="secondary" sx={{ fontWeight: 'bold' }}>
              {parseFloat(product.pricing.takeaway || 0).toFixed(2)}€
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem' }}>
              <DeliveryIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Delivery:
            </Typography>
            <Typography variant="body2" color="info.main" sx={{ fontWeight: 'bold' }}>
              {parseFloat(product.pricing.delivery || 0).toFixed(2)}€
            </Typography>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Typography variant="h6" color="primary">
            {parseFloat(product.price || 0).toFixed(2)}€
          </Typography>
        </Box>
      );
    }
  };

  const renderCategoriesTab = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant="h5">Gestión de Categorías</Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddCategory}
          >
            Nueva Categoría
          </Button>
        </Box>
      </Box>

      {renderMigrationButtons()}

      {categories.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CategoryIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No hay categorías disponibles
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Crea tu primera categoría para comenzar a organizar tu menú
          </Typography>
          <Button 
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddCategory}
          >
            Crear primera categoría
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {categories.map((category, index) => (
            <Grid item xs={12} sm={6} md={4} key={category.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h3" sx={{ mr: 1 }}>
                      {category.icon || '📋'}
                    </Typography>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      {category.name}
                    </Typography>
                    <Box>
                      <Tooltip title="Mover arriba">
                        <IconButton 
                          size="small" 
                          onClick={() => handleMoveCategoryUp(index)}
                          disabled={index === 0}
                        >
                          <ArrowUpIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mover abajo">
                        <IconButton 
                          size="small" 
                          onClick={() => handleMoveCategoryDown(index)}
                          disabled={index === categories.length - 1}
                        >
                          <ArrowDownIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  {category.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {category.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Productos: {products.filter(p => p.categoryId === category.id).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Orden: {category.order || 'Sin definir'}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditCategory(category)}
                  >
                    Editar
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteCategory(category.id, category.name)}
                  >
                    Eliminar
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  const renderProductsTab = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant="h5">Gestión de Productos</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddProduct}
          disabled={categories.length === 0}
        >
          Nuevo Producto
        </Button>
      </Box>

      {renderMigrationButtons()}

      {/* Filtros y Controles de Vista */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel id="category-select-label">Filtrar por Categoría</InputLabel>
            <Select
              labelId="category-select-label"
              value={selectedCategory}
              label="Filtrar por Categoría"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem value={0}>Todas las categorías</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.icon} {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ButtonGroup>
            <Button 
              variant={viewMode === 'list' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('list')}
              startIcon={<ViewListIcon />}
            >
              Lista
            </Button>
            <Button 
              variant={viewMode === 'grid' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('grid')}
              startIcon={<ImageIcon />}
            >
              Cuadrícula
            </Button>
          </ButtonGroup>
        </Box>
      </Paper>

      {/* Lista/Grid de Productos */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : categories.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <RestaurantIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No hay categorías disponibles
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Debes crear al menos una categoría antes de añadir productos
          </Typography>
          <Button 
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setActiveTab(1)}
          >
            Crear categoría
          </Button>
        </Paper>
      ) : viewMode === 'list' ? renderListView() : renderGridView()}
    </Box>
  );

  const renderListView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Producto</TableCell>
            <TableCell>Categoría</TableCell>
            <TableCell>Precios</TableCell>
            <TableCell>Disponibilidad</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => {
              const category = categories.find(cat => cat.id === product.categoryId);
              return (
                <TableRow key={product.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {product.image && (
                        <Box
                          component="img"
                          src={product.image}
                          alt={product.name}
                          sx={{ width: 40, height: 40, borderRadius: 1, mr: 2, objectFit: 'cover' }}
                        />
                      )}
                      <Typography variant="body2">{product.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {category ? `${category.icon} ${category.name}` : 'Sin categoría'}
                  </TableCell>
                  <TableCell>
                    {renderPricingInTable(product)}
                  </TableCell>
                  <TableCell>
                    {product.availability?.local && (
                      <Chip size="small" label="Local" color="primary" sx={{ mr: 0.5, mb: 0.5 }} />
                    )}
                    {product.availability?.takeaway && (
                      <Chip size="small" label="Para llevar" color="secondary" sx={{ mr: 0.5, mb: 0.5 }} />
                    )}
                    {product.availability?.delivery && (
                      <Chip size="small" label="Domicilio" color="info" sx={{ mb: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="small" 
                      label={product.active ? 'Activo' : 'Inactivo'} 
                      color={product.active ? 'success' : 'default'}
                      onClick={() => handleToggleActive(product.id, product.active)}
                      clickable
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      color="primary" 
                      onClick={() => handleEditProduct(product)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => handleDeleteProduct(product.id, product.name)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="body1" color="textSecondary">
                  No hay productos disponibles
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderGridView = () => (
    <Grid container spacing={3}>
      {filteredProducts.length > 0 ? (
        filteredProducts.map((product) => {
          const category = categories.find(cat => cat.id === product.categoryId);
          return (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {!product.active && (
                  <Chip 
                    label="Inactivo" 
                    color="default" 
                    size="small"
                    sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}
                  />
                )}
                <Box 
                  sx={{ 
                    height: 160, 
                    backgroundImage: `url(${product.image || '/api/placeholder/300/200'})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundColor: 'grey.200'
                  }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {product.description && product.description.length > 100 
                      ? `${product.description.substring(0, 100)}...` 
                      : product.description}
                  </Typography>
                  
                  {renderPricingInGrid(product)}

                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {category ? `${category.icon} ${category.name}` : 'Sin categoría'}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    {product.availability?.local && (
                      <Chip size="small" label="Local" color="primary" sx={{ mr: 0.5, mb: 0.5 }} />
                    )}
                    {product.availability?.takeaway && (
                      <Chip size="small" label="Para llevar" color="secondary" sx={{ mr: 0.5, mb: 0.5 }} />
                    )}
                    {product.availability?.delivery && (
                      <Chip size="small" label="Domicilio" color="info" sx={{ mb: 0.5 }} />
                    )}
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditProduct(product)}
                  >
                    Editar
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteProduct(product.id, product.name)}
                  >
                    Eliminar
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })
      ) : (
        <Grid item xs={12}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No hay productos disponibles para esta categoría
            </Typography>
          </Paper>
        </Grid>
      )}
    </Grid>
  );

  return (
    <Box sx={{ pb: 4 }}>
      {/* Encabezado Principal */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>Gestión de Carta</Typography>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={`Productos (${products.length})`} />
          <Tab label={`Categorías (${categories.length})`} />
          <Tab label="Optimización de Imágenes" />
        </Tabs>
      </Box>

      {/* Contenido de las pestañas */}
      {activeTab === 0 && renderProductsTab()}
      {activeTab === 1 && renderCategoriesTab()}
      {activeTab === 2 && <ImageMigrationPanel />}

      {/* Diálogo para Añadir/Editar Producto */}
      <Dialog 
        open={openProductDialog} 
        onClose={() => setOpenProductDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {currentProduct?.id ? 'Editar Producto' : 'Nuevo Producto'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="name"
                label="Nombre del producto"
                value={currentProduct?.name || ''}
                onChange={handleProductChange}
                fullWidth
                required
                margin="normal"
              />
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="product-category-label">Categoría</InputLabel>
                <Select
                  labelId="product-category-label"
                  name="categoryId"
                  value={currentProduct?.categoryId || ''}
                  onChange={handleProductChange}
                  label="Categoría"
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.icon} {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* 🆕 CAMPOS DE PRECIOS MÚLTIPLES */}
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                    <EuroIcon sx={{ mr: 1 }} />
                    Precios por Tipo de Pedido
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Precio Local (en restaurante)"
                        type="number"
                        value={currentProduct?.pricing?.local || ''}
                        onChange={(e) => handlePricingChange('local', e.target.value)}
                        fullWidth
                        margin="normal"
                        inputProps={{ step: 0.01, min: 0 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <HomeIcon color="primary" />
                            </InputAdornment>
                          ),
                          endAdornment: <InputAdornment position="end">€</InputAdornment>
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Precio Takeaway (para llevar)"
                        type="number"
                        value={currentProduct?.pricing?.takeaway || ''}
                        onChange={(e) => handlePricingChange('takeaway', e.target.value)}
                        fullWidth
                        margin="normal"
                        inputProps={{ step: 0.01, min: 0 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <ShoppingBagIcon color="secondary" />
                            </InputAdornment>
                          ),
                          endAdornment: <InputAdornment position="end">€</InputAdornment>
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Precio Delivery (entrega a domicilio)"
                        type="number"
                        value={currentProduct?.pricing?.delivery || ''}
                        onChange={(e) => handlePricingChange('delivery', e.target.value)}
                        fullWidth
                        margin="normal"
                        inputProps={{ step: 0.01, min: 0 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <DeliveryIcon color="info" />
                            </InputAdornment>
                          ),
                          endAdornment: <InputAdornment position="end">€</InputAdornment>
                        }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
              
              <Box sx={{ mt: 2, mb: 2 }}>
                {previewImage && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Vista previa:</Typography>
                    <Box
                      component="img"
                      src={previewImage}
                      alt="Vista previa"
                      sx={{ 
                        width: '100%', 
                        height: 150, 
                        objectFit: 'cover',
                        borderRadius: 1
                      }}
                    />
                  </Box>
                )}
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<ImageIcon />}
                >
                  {previewImage ? 'Cambiar imagen' : 'Subir imagen'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageSelect}
                  />
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="description"
                label="Descripción"
                value={currentProduct?.description || ''}
                onChange={handleProductChange}
                fullWidth
                multiline
                rows={4}
                margin="normal"
              />
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Disponibilidad
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={currentProduct?.availability?.local || false}
                        onChange={() => handleAvailabilityChange('local')}
                        name="local"
                      />
                    }
                    label="Disponible para consumo en local"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={currentProduct?.availability?.takeaway || false}
                        onChange={() => handleAvailabilityChange('takeaway')}
                        name="takeaway"
                      />
                    }
                    label="Disponible para llevar"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={currentProduct?.availability?.delivery || false}
                        onChange={() => handleAvailabilityChange('delivery')}
                        name="delivery"
                      />
                    }
                    label="Disponible para entrega a domicilio"
                  />
                </FormGroup>
              </Box>
              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={currentProduct?.active || false}
                      onChange={(e) => setCurrentProduct(prev => ({ ...prev, active: e.target.checked }))}
                      name="active"
                    />
                  }
                  label="Producto activo (visible en la carta)"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProductDialog(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            startIcon={<SaveIcon />}
            onClick={handleSaveProduct}
            disabled={isLoading}
          >
            {isLoading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para Añadir/Editar Categoría */}
      <Dialog 
        open={openCategoryDialog} 
        onClose={() => setOpenCategoryDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {currentCategory?.id ? 'Editar Categoría' : 'Nueva Categoría'}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            name="name"
            label="Nombre de la categoría"
            value={currentCategory?.name || ''}
            onChange={handleCategoryChange}
            fullWidth
            required
            margin="normal"
          />
          
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Icono de la categoría
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h2" sx={{ mr: 2 }}>
                {currentCategory?.icon || '📋'}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setShowIconPicker(!showIconPicker)}
              >
                Cambiar icono
              </Button>
            </Box>
            
            {showIconPicker && (
              <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                <Tabs 
                  value={selectedIconCategory}
                  onChange={(e, newValue) => setSelectedIconCategory(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 2 }}
                >
                  {Object.keys(AVAILABLE_ICONS).map((category) => (
                    <Tab key={category} label={category} value={category} />
                  ))}
                </Tabs>
                
                <Grid container spacing={1}>
                  {AVAILABLE_ICONS[selectedIconCategory].map((icon, index) => (
                    <Grid item key={index}>
                      <IconButton
                        onClick={() => handleIconSelect(icon)}
                        sx={{ 
                          fontSize: '1.5rem',
                          border: currentCategory?.icon === icon ? '2px solid' : '1px solid',
                          borderColor: currentCategory?.icon === icon ? 'primary.main' : 'grey.300',
                          bgcolor: currentCategory?.icon === icon ? 'primary.50' : 'transparent'
                        }}
                      >
                        {icon}
                      </IconButton>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}
          </Box>

          <TextField
            name="description"
            label="Descripción (opcional)"
            value={currentCategory?.description || ''}
            onChange={handleCategoryChange}
            fullWidth
            multiline
            rows={3}
            margin="normal"
          />
          
          {previewImage && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Vista previa:</Typography>
              <Box
                component="img"
                src={previewImage}
                alt="Vista previa"
                sx={{ 
                  width: '100%', 
                  height: 150, 
                  objectFit: 'cover',
                  borderRadius: 1
                }}
              />
            </Box>
          )}
          
          <Button
            variant="outlined"
            component="label"
            startIcon={<ImageIcon />}
            sx={{ mt: 1 }}
          >
            {previewImage ? 'Cambiar imagen' : 'Subir imagen (opcional)'}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageSelect}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            startIcon={<SaveIcon />}
            onClick={handleSaveCategory}
            disabled={!currentCategory?.name || isLoading}
          >
            {isLoading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MenuAdminComponent;